/**
 * STORAGE USAGE CARD
 * 
 * Visual component showing user's current storage usage.
 * Features:
 * - Progress bar with color coding (green → yellow → red)
 * - Current tier badge (subscription or beta tester)
 * - Upgrade prompt when approaching limit
 * - Detailed breakdown (optional)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HardDrive, TrendingUp, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Loader2, Crown, CreditCard, ExternalLink } from 'lucide-react';
import { useStorageUsage } from '../hooks/useStorageUsage';
import { formatBytes, calculateYearlySavings } from '../constants/storageTiers';
import { auth } from '../firebase/config';

// Cloud Function URL for portal session
const PORTAL_FUNCTION_URL = 'https://us-central1-polish-all-the-things--app.cloudfunctions.net/createPortalSession';

interface StorageUsageCardProps {
  showUpgradeButton?: boolean;
  showDetails?: boolean;
  compact?: boolean;
  onUpgradeClick?: () => void;
}

export default function StorageUsageCard({ 
  showUpgradeButton = true, 
  showDetails = false,
  compact = false,
  onUpgradeClick 
}: StorageUsageCardProps) {
  const navigate = useNavigate();
  const {
    tier,
    usedBytes,
    percentUsed,
    usedFormatted,
    limitFormatted,
    isLoading,
    isApproachingLimit,
    isOverLimit,
    isBetaTester,
    hasActiveSubscription,
    subscription,
    nextTier,
    recalculate,
  } = useStorageUsage();
  
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  
  // Determine progress bar color
  const getProgressColor = () => {
    if (isBetaTester) return 'bg-purple-500'; // Special color for beta testers
    if (hasActiveSubscription) return 'bg-pink-500'; // Special color for subscribers
    if (isOverLimit) return 'bg-red-500';
    if (percentUsed >= 90) return 'bg-red-500';
    if (percentUsed >= 70) return 'bg-amber-500';
    return 'bg-green-500';
  };
  
  // Handle recalculate click
  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      await recalculate();
    } finally {
      setIsRecalculating(false);
    }
  };

  // Handle upgrade click - go to pricing page
  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/pricing');
    }
  };

  // Handle manage subscription - open Stripe portal
  const handleManageSubscription = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error('No user logged in');
      return;
    }

    setIsOpeningPortal(true);
    try {
      const response = await fetch(PORTAL_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open subscription management');
      }

      const { url } = await response.json();

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      alert('Unable to open subscription management. Please try again.');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  // Format subscription interval for display
  const formatInterval = (interval: string | null) => {
    if (interval === 'yearly') return 'Annual';
    if (interval === 'monthly') return 'Monthly';
    return '';
  };
  
  if (isLoading) {
    return (
      <div className={`glass-card ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-glass-muted animate-spin" />
          <span className="text-glass-muted font-body">Loading storage info...</span>
        </div>
      </div>
    );
  }
  
  // Compact version (for sidebar or inline use)
  if (compact) {
    return (
      <div className="bg-slate-800/40 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-glass-muted" />
            <span className="text-sm font-medium text-glass font-body">Storage</span>
          </div>
          <span className="text-xs text-glass-muted font-body">
            {usedFormatted} / {limitFormatted}
          </span>
        </div>
        
        <div className="progress-bar-track h-2">
          <div 
            className={`h-full rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        
        {isOverLimit && (
          <p className="text-xs text-red-400 mt-2 font-body">
            Storage full! Delete photos or upgrade to continue.
          </p>
        )}
      </div>
    );
  }
  
  // Full version
  return (
    <div className="glass-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-700/50 flex items-center justify-center">
            <HardDrive className="w-5 h-5 text-glass" />
          </div>
          <div>
            <h3 className="font-bold text-glass font-body">Photo Storage</h3>
            {hasActiveSubscription ? (
              <p className="text-sm text-pink-400 font-body flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {tier.name} — <span className="font-medium">{formatInterval(subscription?.interval || null)} Subscriber</span>
              </p>
            ) : isBetaTester ? (
              <p className="text-sm text-purple-400 font-body">
                ⭐ Beta Tester — <span className="font-medium">Unlimited Storage</span>
              </p>
            ) : (
              <p className="text-sm text-glass-secondary font-body">
                You are currently using <span className="font-medium text-glass">{tier.emoji} {tier.name}</span>
              </p>
            )}
          </div>
        </div>
        
        {/* Recalculate button */}
        <button
          onClick={handleRecalculate}
          disabled={isRecalculating}
          className="p-2 text-glass-muted hover:text-glass hover:bg-slate-700/50 rounded-lg transition-default"
          title="Recalculate storage usage"
        >
          <RefreshCw className={`w-4 h-4 ${isRecalculating ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Usage display */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-2xl font-bold text-glass font-body">{usedFormatted}</span>
          <span className="text-glass-secondary font-body">
            {isBetaTester ? '(unlimited)' : `of ${limitFormatted}`}
          </span>
        </div>
        
        {/* Progress bar - show for subscribers and free users, not beta testers */}
        {!isBetaTester && (
          <div className="progress-bar-track h-3">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${Math.min(100, percentUsed)}%` }}
            />
          </div>
        )}
        
        {isBetaTester ? (
          <p className="text-sm text-purple-400 mt-2 font-body">
            🎉 Thank you for being a beta tester! You have unlimited storage during the beta period.
          </p>
        ) : hasActiveSubscription ? (
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-glass-muted font-body">{percentUsed}% used</span>
            {percentUsed < 100 && (
              <span className="text-sm text-glass-muted font-body">
                {formatBytes(tier.storageLimitBytes - usedBytes)} remaining
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-glass-muted font-body">{percentUsed}% used</span>
            {percentUsed < 100 && (
              <span className="text-sm text-glass-muted font-body">
                {formatBytes(tier.storageLimitBytes - usedBytes)} remaining
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Warning message */}
      {isOverLimit && (
        <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-300 font-body">Storage limit reached</p>
            <p className="text-sm text-red-300/80 font-body">
              You've used all your storage. Delete some photos or upgrade to add more.
            </p>
          </div>
        </div>
      )}
      
      {isApproachingLimit && !isOverLimit && (
        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-300 font-body">Running low on storage</p>
            <p className="text-sm text-amber-300/80 font-body">
              You're using {percentUsed}% of your storage. Consider upgrading soon.
            </p>
          </div>
        </div>
      )}
      
      {/* Subscription status for active subscribers */}
      {hasActiveSubscription && subscription?.cancelAtPeriodEnd && (
        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-300 font-body">Subscription ending</p>
            <p className="text-sm text-amber-300/80 font-body">
              Your subscription will end at the current billing period. You'll be moved to the free tier after that.
            </p>
          </div>
        </div>
      )}
      
      {/* Manage Subscription button for active subscribers */}
      {hasActiveSubscription && (
        <div className="mb-4">
          <button
            onClick={handleManageSubscription}
            disabled={isOpeningPortal}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700/50 text-glass hover:bg-slate-600/50 rounded-lg font-medium transition-default font-body"
          >
            {isOpeningPortal ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Opening...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                Manage Subscription
                <ExternalLink className="w-3 h-3 ml-1" />
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Upgrade prompt - hide for beta testers and subscribers who already have the top tier */}
      {showUpgradeButton && nextTier && !isBetaTester && (
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                <span className="font-medium text-glass font-body">
                  {hasActiveSubscription ? 'Upgrade to' : 'Get'} {nextTier.name}
                </span>
              </div>
              <p className="text-sm text-glass-secondary font-body mb-2">
                Get {nextTier.storageLimitMB >= 1024 ? `${nextTier.storageLimitMB / 1024} GB` : `${nextTier.storageLimitMB} MB`} of storage for ${nextTier.priceMonthly}/month
              </p>
              {nextTier.priceYearly > 0 && (
                <p className="text-xs text-purple-300 font-body">
                  Save ${calculateYearlySavings(nextTier)} with yearly billing
                </p>
              )}
            </div>
            <button
              onClick={handleUpgradeClick}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all font-body whitespace-nowrap"
            >
              {hasActiveSubscription ? 'Upgrade' : 'Subscribe'}
            </button>
          </div>
        </div>
      )}
      
      {/* Details toggle */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-slate-600">
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center gap-2 text-sm text-glass-secondary hover:text-glass font-body"
          >
            {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showBreakdown ? 'Hide details' : 'Show details'}
          </button>
          
          {showBreakdown && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-glass-muted font-body">Current tier</span>
                <span className="text-glass font-body">{tier.emoji} {tier.name}</span>
              </div>
              {hasActiveSubscription && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-glass-muted font-body">Billing</span>
                  <span className="text-glass font-body">{formatInterval(subscription?.interval || null)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-glass-muted font-body">Storage limit</span>
                <span className="text-glass font-body">{limitFormatted}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-glass-muted font-body">Used</span>
                <span className="text-glass font-body">{usedFormatted}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-glass-muted font-body">Available</span>
                <span className="text-glass font-body">{formatBytes(Math.max(0, tier.storageLimitBytes - usedBytes))}</span>
              </div>
              {tier.priceMonthly > 0 && !hasActiveSubscription && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-glass-muted font-body">Monthly cost</span>
                  <span className="text-glass font-body">${tier.priceMonthly}/mo</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
